<template>
    <div class="relative" ref="containerRef">
        <!-- Trigger -->
        <div
            ref="triggerRef"
            @click="togglePanel"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md cursor-pointer flex items-center justify-between bg-white dark:bg-gray-700 dark:text-white transition-colors focus-within:ring-primary-500 focus-within:border-primary-500"
            :class="{ 'bg-gray-100 dark:bg-gray-600': disabled }"
        >
            <span class="flex items-center gap-2 truncate">
                <CalendarIcon class="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span v-if="hasValue" class="truncate text-sm">
                    {{ displayRange }}
                </span>
                <span
                    v-else
                    class="text-gray-500 dark:text-gray-400 truncate text-sm"
                >
                    {{ placeholder || t("common.dateRange") }}
                </span>
            </span>
            <button
                v-if="hasValue && !disabled"
                @click.stop="clear"
                class="ml-2 text-gray-400 hover:text-danger-500 transition-colors flex-shrink-0"
                :title="t('common.clear')"
            >
                <X class="w-4 h-4" />
            </button>
        </div>

        <!-- Calendar panel (teleported so page/modal overflow never clips it) -->
        <Teleport to="body">
            <div
                v-if="isOpen"
                @click="closePanel"
                class="fixed inset-0 z-40"
            ></div>
            <div
                v-if="isOpen"
                ref="panelRef"
                data-date-range-panel
                class="fixed z-50 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg p-3 select-none"
                :style="panelStyle"
            >
                <!-- Month header -->
                <div
                    class="flex items-center justify-between mb-2 text-gray-700 dark:text-gray-200"
                >
                    <button
                        @click="changeMonth(-1)"
                        class="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        :title="t('common.previous')"
                    >
                        <ChevronLeft class="w-4 h-4" />
                    </button>
                    <span class="text-sm font-medium">
                        {{ monthLabel }}
                    </span>
                    <button
                        @click="changeMonth(1)"
                        class="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        :title="t('common.next')"
                    >
                        <ChevronRight class="w-4 h-4" />
                    </button>
                </div>

                <!-- Weekday header -->
                <div
                    class="grid grid-cols-7 mb-1 text-center text-xs text-gray-400 dark:text-gray-500"
                >
                    <span v-for="day in weekdayLabels" :key="day">{{
                        day
                    }}</span>
                </div>

                <!-- Day grid -->
                <div class="grid grid-cols-7 gap-y-0.5 text-center text-sm">
                    <button
                        v-for="cell in calendarCells"
                        :key="cell.key"
                        @click="selectDay(cell)"
                        class="h-8 rounded-md transition-colors"
                        :class="dayClasses(cell)"
                    >
                        {{ cell.dayOfMonth }}
                    </button>
                </div>

                <!-- Actions -->
                <div
                    class="flex items-center justify-between mt-3 pt-2 border-t border-gray-200 dark:border-gray-700"
                >
                    <button
                        @click="selectToday"
                        class="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                        {{ t("common.today") }}
                    </button>
                    <button
                        @click="clear"
                        class="px-3 py-1.5 text-sm rounded-md text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                    >
                        {{ t("common.clear") }}
                    </button>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import dayjs from "dayjs";
import "dayjs/locale/id";
import { CalendarIcon, ChevronLeft, ChevronRight, X } from "@lucide/vue";

export interface DateRangeValue {
    date_from: string | null;
    date_to: string | null;
}

const props = defineProps<{
    modelValue: DateRangeValue | null;
    placeholder?: string;
    disabled?: boolean;
}>();

const emit = defineEmits<{
    "update:modelValue": [value: DateRangeValue | null];
}>();

const { t, locale } = useI18n();

const containerRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLElement | null>(null);
const panelRef = ref<HTMLElement | null>(null);
const isOpen = ref(false);
const viewMonth = ref(dayjs().startOf("month"));
// Pending selection: first click sets start, second click completes the range
const pendingStart = ref<string | null>(null);

const panelStyle = reactive<Record<string, string>>({});
const PANEL_WIDTH = 280;

const fmt = "YYYY-MM-DD";

const hasValue = computed(() => {
    return Boolean(props.modelValue?.date_from || props.modelValue?.date_to);
});

const displayRange = computed(() => {
    const from = props.modelValue?.date_from;
    const to = props.modelValue?.date_to;
    const format = (value: string) => dayjs(value).format("DD/MM/YYYY");
    if (from && to) return `${format(from)} – ${format(to)}`;
    if (from) return `${format(from)} – …`;
    if (to) return `… – ${format(to)}`;
    return "";
});

const monthLabel = computed(() => {
    const localeMap: Record<string, string> = { id: "id", en: "en" };
    return viewMonth.value
        .locale(localeMap[locale.value] ?? "id")
        .format("MMMM YYYY");
});

const weekdayLabels = computed(() => {
    const localeCode = locale.value === "en" ? "en" : "id";
    const base = dayjs().locale(localeCode);
    // Monday-first week
    return Array.from({ length: 7 }, (_, i) =>
        base.day((i + 1) % 7).format("dd"),
    );
});

interface CalendarCell {
    key: string;
    date: string;
    dayOfMonth: number;
    inCurrentMonth: boolean;
}

const calendarCells = computed<CalendarCell[]>(() => {
    const first = viewMonth.value.startOf("month");
    // Monday-first offset (dayjs: Sunday=0 … Saturday=6 → Monday=0 … Sunday=6)
    const offset = (first.day() + 6) % 7;
    const gridStart = first.subtract(offset, "day");

    return Array.from({ length: 42 }, (_, i) => {
        const day = gridStart.add(i, "day");
        return {
            key: day.format(fmt),
            date: day.format(fmt),
            dayOfMonth: day.date(),
            inCurrentMonth: day.month() === viewMonth.value.month(),
        };
    });
});

function dayClasses(cell: CalendarCell): Record<string, boolean> {
    const { date_from, date_to } = props.modelValue ?? {};
    const start = pendingStart.value ?? date_from;
    const end = pendingStart.value ? null : date_to;

    const isEndpoint = Boolean(
        start && end && (cell.date === start || cell.date === end),
    );
    const isPendingStart = Boolean(start && !end && cell.date === start);
    const isInRange = Boolean(
        start && end && cell.date > start && cell.date < end,
    );

    return {
        "text-gray-300 dark:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700":
            !cell.inCurrentMonth,
        "hover:bg-primary-50 dark:hover:bg-primary-900/30 text-gray-700 dark:text-gray-200":
            cell.inCurrentMonth,
        "bg-primary-600 text-white hover:bg-primary-700":
            isEndpoint || isPendingStart,
        "bg-primary-50 dark:bg-primary-900/40 text-primary-700 dark:text-primary-200":
            isInRange,
    };
}

function togglePanel() {
    if (props.disabled) return;
    isOpen.value ? closePanel() : openPanel();
}

async function openPanel() {
    isOpen.value = true;
    pendingStart.value = props.modelValue?.date_from ?? null;
    const anchor = props.modelValue?.date_from ?? props.modelValue?.date_to;
    if (anchor) viewMonth.value = dayjs(anchor).startOf("month");
    await nextTick();
    updatePanelPosition();
}

function closePanel() {
    // A dangling start without an end is discarded
    pendingStart.value = null;
    isOpen.value = false;
}

function changeMonth(delta: number) {
    viewMonth.value = viewMonth.value.add(delta, "month");
}

function emitRange(from: string | null, to: string | null) {
    emit("update:modelValue", { date_from: from, date_to: to });
}

function selectDay(cell: CalendarCell) {
    if (!pendingStart.value) {
        pendingStart.value = cell.date;
        emitRange(cell.date, null);
        return;
    }

    if (cell.date < pendingStart.value) {
        // Clicking before the current start restarts the range
        pendingStart.value = cell.date;
        emitRange(cell.date, null);
        return;
    }

    emitRange(pendingStart.value, cell.date);
    pendingStart.value = null;
    isOpen.value = false;
}

function selectToday() {
    const today = dayjs().format(fmt);
    pendingStart.value = null;
    emitRange(today, today);
    isOpen.value = false;
}

function clear() {
    pendingStart.value = null;
    emitRange(null, null);
    isOpen.value = false;
}

function updatePanelPosition() {
    const trigger = triggerRef.value;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const panelHeight = 360;

    if (spaceBelow >= panelHeight || spaceBelow >= rect.top) {
        panelStyle.top = `${rect.bottom + 4}px`;
        panelStyle.bottom = "auto";
    } else {
        panelStyle.top = "auto";
        panelStyle.bottom = `${window.innerHeight - rect.top + 4}px`;
    }
    panelStyle.left = `${Math.max(rect.left, 8)}px`;
    panelStyle.width = `${Math.max(rect.width, PANEL_WIDTH)}px`;
}

function onDocumentClick(event: MouseEvent) {
    const target = event.target as Node;
    const insideTrigger = Boolean(containerRef.value?.contains(target));
    const insidePanel = Boolean(panelRef.value?.contains(target));
    if (isOpen.value && !insideTrigger && !insidePanel) {
        closePanel();
    }
}

function onKeydown(event: KeyboardEvent) {
    if (event.key === "Escape" && isOpen.value) {
        closePanel();
    }
}

function onWindowChange() {
    if (isOpen.value) updatePanelPosition();
}

onMounted(() => {
    document.addEventListener("click", onDocumentClick);
    document.addEventListener("keydown", onKeydown);
    window.addEventListener("resize", onWindowChange);
    window.addEventListener("scroll", onWindowChange, true);
});

onUnmounted(() => {
    document.removeEventListener("click", onDocumentClick);
    document.removeEventListener("keydown", onKeydown);
    window.removeEventListener("resize", onWindowChange);
    window.removeEventListener("scroll", onWindowChange, true);
});
</script>
